import { useEffect, useMemo, useState } from "react";
import {
  inviteIdentity,
  linkFoxline,
  resendInvite,
  searchFoxlineEmployees,
  unlinkFoxline,
} from "./api";
import { PRESET_LABELS, brandLabel, matchStore, storeLabel, storesForBrand } from "./identityScope";

function t(lang, en, gr) {
  return lang === "gr" ? gr : en;
}

function foxlineLabel(emp) {
  if (!emp) return "";
  const name = emp.name || [emp.first_name, emp.last_name].filter(Boolean).join(" ");
  return [name, emp.employee_code, emp.store, emp.specialty].filter(Boolean).join(" · ");
}

const inputStyle = {
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--text-primary)",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 13,
};

const EMPTY_LIST = [];

export function InviteOpsUser({ lang, catalog, onDone }) {
  const brands = catalog?.brands || EMPTY_LIST;
  const stores = catalog?.stores || EMPTY_LIST;
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState(null);
  const [email, setEmail] = useState("");
  const [preset, setPreset] = useState("crew");
  const [brandId, setBrandId] = useState("");
  const [scope, setScope] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const brandStores = useMemo(() => storesForBrand(stores, brandId), [brandId, stores]);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      setHits([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchFoxlineEmployees(needle)
        .then(setHits)
        .catch(() => setHits([]));
    }, 280);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    if (picked?.suggested_preset) setPreset(picked.suggested_preset);
    const hit = picked?.store ? matchStore(stores, picked.store) : undefined;
    setBrandId(hit?.brand_id != null ? String(hit.brand_id) : "");
    setScope(hit ? hit.location_name : "");
    if (picked?.email) setEmail(picked.email);
  }, [picked, stores]);

  async function onInvite(e) {
    e.preventDefault();
    if (!picked) return;
    setBusy(true);
    setError("");
    try {
      await inviteIdentity({
        foxline_employee_id: picked.employee_id,
        email: email.trim(),
        preset,
        ops_role: preset === "store_manager" ? "store_manager" : "employee",
        scope_value: preset === "store_manager" ? scope || picked.store : null,
        name: picked.name || undefined,
      });
      setQ("");
      setHits([]);
      setPicked(null);
      setEmail("");
      onDone?.(t(lang, `Invite sent to ${email.trim()}`, `Η πρόσκληση στάλθηκε στο ${email.trim()}`));
    } catch (err) {
      setError(err.message || t(lang, "Invite failed", "Η πρόσκληση απέτυχε"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={onInvite}
      style={{
        borderRadius: 14,
        border: "1px solid var(--border)",
        background: "var(--surface)",
        padding: 16,
      }}
    >
      <h2 style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>
        {t(lang, "Invite Ops user (Foxline)", "Πρόσκληση χρήστη Ops (Foxline)")}
      </h2>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>
        {t(
          lang,
          "Search PRSN, send a set-password email. Job title and hours stay in Foxline.",
          "Αναζήτηση PRSN, αποστολή email για κωδικό. Θέση και ώρες μένουν στο Foxline."
        )}
      </p>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
          {t(lang, "Foxline employee", "Υπάλληλος Foxline")}
          <input
            value={picked ? foxlineLabel(picked) : q}
            onChange={(e) => {
              setPicked(null);
              setQ(e.target.value);
            }}
            placeholder={t(lang, "Name, AFM, or store", "Όνομα, AFM ή κατάστημα")}
            style={inputStyle}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
          {t(lang, "Login email", "Email σύνδεσης")}
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@gmail.com"
            style={inputStyle}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
          {t(lang, "Ops role", "Ρόλος Ops")}
          <select value={preset} onChange={(e) => setPreset(e.target.value)} style={inputStyle}>
            <option value="crew">{PRESET_LABELS.crew}</option>
            <option value="chef">{PRESET_LABELS.chef}</option>
            <option value="store_manager">{PRESET_LABELS.store_manager}</option>
          </select>
        </label>
        {preset === "store_manager" && (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
              {t(lang, "Brand", "Μάρκα")}
              <select
                required
                value={brandId}
                onChange={(e) => {
                  setBrandId(e.target.value);
                  setScope("");
                }}
                style={inputStyle}
              >
                <option value="">{t(lang, "Select brand", "Επιλέξτε μάρκα")}</option>
                {brands.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {brandLabel(b)}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--text-muted)" }}>
              {t(lang, "Store", "Κατάστημα")}
              <select
                required
                disabled={!brandId}
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                style={inputStyle}
              >
                <option value="">
                  {brandId
                    ? t(lang, "Select store", "Επιλέξτε κατάστημα")
                    : t(lang, "Select brand first", "Πρώτα μάρκα")}
                </option>
                {brandStores.map((s) => (
                  <option key={s.location_name} value={s.location_name}>
                    {storeLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button type="submit" className="btn-base btn-green" disabled={busy || !picked || !email.trim()} style={{ width: "100%" }}>
            {busy ? t(lang, "Sending…", "Αποστολή…") : t(lang, "Send invite", "Αποστολή πρόσκλησης")}
          </button>
        </div>
      </div>
      {!picked && hits.length > 0 && (
        <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, border: "1px solid var(--border)", borderRadius: 10, maxHeight: 180, overflowY: "auto" }}>
          {hits.map((emp) => (
            <li key={emp.employee_id}>
              <button
                type="button"
                onClick={() => setPicked(emp)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--text-primary)",
                }}
              >
                {foxlineLabel(emp)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: "var(--danger)" }}>{error}</div>
      )}
    </form>
  );
}

export function FoxlineRowActions({ lang, user, onDone }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState(false);
  const fox = user.foxline;

  useEffect(() => {
    if (!linking) return undefined;
    const needle = q.trim();
    if (needle.length < 2) {
      setHits([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchFoxlineEmployees(needle)
        .then(setHits)
        .catch(() => setHits([]));
    }, 280);
    return () => clearTimeout(timer);
  }, [q, linking]);

  async function run(fn, okMessage) {
    setBusy(true);
    try {
      await fn();
      setLinking(false);
      setQ("");
      setHits([]);
      onDone?.(okMessage);
    } catch (err) {
      onDone?.(null, err.message || t(lang, "Request failed", "Αποτυχία αιτήματος"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      {fox?.employee_id ? (
        <>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {foxlineLabel(fox)}
          </span>
          <button
            type="button"
            className="btn-base btn-outline btn-small"
            disabled={busy}
            onClick={() => run(() => unlinkFoxline(user.id), t(lang, "Foxline unlinked", "Αποσύνδεση Foxline"))}
          >
            {t(lang, "Unlink", "Αποσύνδεση")}
          </button>
        </>
      ) : linking ? (
        <div style={{ minWidth: 220 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t(lang, "Search Foxline…", "Αναζήτηση Foxline…")}
            style={{ ...inputStyle, width: "100%" }}
          />
          {hits.length > 0 && (
            <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, border: "1px solid var(--border)", borderRadius: 8, maxHeight: 140, overflowY: "auto" }}>
              {hits.map((emp) => (
                <li key={emp.employee_id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(
                      () => linkFoxline(user.id, emp.employee_id),
                      t(lang, "Foxline linked", "Σύνδεση Foxline")
                    )}
                    style={{ width: "100%", textAlign: "left", border: "none", background: "transparent", padding: "6px 10px", cursor: "pointer", fontSize: 12 }}
                  >
                    {foxlineLabel(emp)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <button type="button" className="btn-base btn-outline btn-small" onClick={() => setLinking(true)}>
          {t(lang, "Link Foxline", "Σύνδεση Foxline")}
        </button>
      )}
      {user.awaiting_password && (
        <button
          type="button"
          className="btn-base btn-outline btn-small"
          disabled={busy}
          onClick={() => run(
            () => resendInvite(user.id),
            t(lang, `Invite resent to ${user.email}`, `Η πρόσκληση ξαναστάλθηκε στο ${user.email}`)
          )}
        >
          {t(lang, "Resend invite", "Επανάληψη πρόσκλησης")}
        </button>
      )}
    </div>
  );
}
